"use client";

import { datadogRum } from "@datadog/browser-rum";
import { useEffect } from "react";

import { useSession } from "@/hooks/use-session";

/**
 * Keeps the Datadog RUM user in sync with the session. RUM itself is
 * initialized at module load in instrumentation-client.ts, and views (hence
 * sessions) are started by <DatadogRumViews />. This component is mounted only
 * in the (platform) layout, since that is the only route group with a signed-in
 * user to attribute a session to; elsewhere sessions stay anonymous. During
 * admin impersonation the RUM user is cleared so sessions are not attributed to
 * the impersonated user
 * (mirrors the PostHog identifier). No-op until RUM is initialized.
 */
export function DatadogRum() {
  const { user, session, isLoading } = useSession();
  const isImpersonating = Boolean(session?.session?.impersonatedBy);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isImpersonating || !user?.id) {
      datadogRum.clearUser();
      return;
    }

    datadogRum.setUser({
      id: user.id,
      email: user.email ?? undefined,
      name: user.name ?? undefined,
    });
  }, [isLoading, isImpersonating, user?.id, user?.email, user?.name]);

  return null;
}
